/**
 * The one way the cockpit re-reads something every N seconds.
 *
 * - A tick only reads while the page is awake (tab visible, browser online): a hidden tab has
 *   nobody to show it to, and an offline browser would only collect « Failed to fetch ».
 * - A tick skipped meanwhile is made up once, at the wake: when the tab is seen again or the
 *   network comes back (`online`). The network coming back always re-reads once: a read that
 *   failed during the cut is caught up without waiting for the next tick.
 * - Every running read is registered, so a sign-out (even a session that just expired) stops
 *   them all at once through {@link stopAllPeriodicReads} — no reader keeps polling anonymously.
 */
import { isOnline, isPageAwake, isTabVisible } from './connectivity';

export interface PeriodicReadOptions {
  /** Interval between two reads, in ms. */
  everyMs: number;
  /** The read. Its own errors are its business (see `logReadFailure`). */
  read: () => void;
  /**
   * Catch up at the wake (default true). False when something else already re-reads then —
   * a Realtime sync's `onChange` runs at every wake-up.
   */
  catchUpOnWake?: boolean;
}

const running = new Set<() => void>();

/** Start re-reading; returns the stop (idempotent). Does not read at once: the caller's first load does. */
export function startPeriodicRead({ everyMs, read, catchUpOnWake = true }: PeriodicReadOptions): () => void {
  if (typeof window === 'undefined') return () => undefined;
  let missed = false;

  const tick = () => {
    if (isPageAwake()) read();
    else missed = true;
  };
  const onOnline = () => {
    if (!catchUpOnWake) return;
    if (isTabVisible()) {
      missed = false;
      read();
    } else missed = true;
  };
  const onVisibility = () => {
    if (!catchUpOnWake || !missed || !isTabVisible() || !isOnline()) return;
    missed = false;
    read();
  };

  const timer = window.setInterval(tick, everyMs);
  window.addEventListener('online', onOnline);
  document.addEventListener('visibilitychange', onVisibility);

  let stopped = false;
  const stop = () => {
    if (stopped) return;
    stopped = true;
    window.clearInterval(timer);
    window.removeEventListener('online', onOnline);
    document.removeEventListener('visibilitychange', onVisibility);
    running.delete(stop);
  };
  running.add(stop);
  return stop;
}

/** Stop every periodic read — called on sign-out with the Realtime teardown. */
export function stopAllPeriodicReads(): void {
  for (const stop of [...running]) stop();
}
